import {
	Input,
	Textarea,
	makeStyles,
	mergeClasses,
	tokens,
} from '@fluentui/react-components';
import {
	forwardRef,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
	type ChangeEvent,
	type KeyboardEvent,
	type MouseEvent as ReactMouseEvent,
	type SyntheticEvent,
} from 'react';
import { flushSync } from 'react-dom';

export interface SmartVariableOption {
	label: string;
	value: string;
}

export interface SmartVariablesProps {
	value: string;
	onChange: (value: string) => void;
	options: SmartVariableOption[];
	placeholder?: string;
	/** Character sequence that opens the option list. Defaults to "{{". */
	trigger?: string;
	disabled?: boolean;
	rows?: number;
	id?: string;
	name?: string;
	ariaLabel?: string;
	className?: string;
	textareaClassName?: string;
	filterPlaceholder?: string;
	emptyMessage?: string;
	resize?: 'none' | 'horizontal' | 'vertical' | 'both';
}

const DEFAULT_TRIGGER = '{{';

interface CaretCoordinates {
	top: number;
	lineHeight: number;
}

function getCaretCoordinates(textarea: HTMLTextAreaElement, position: number): CaretCoordinates {
	const style = window.getComputedStyle(textarea);
	const mirror = document.createElement('div');
	const properties = [
		'box-sizing',
		'width',
		'border-top-width',
		'border-right-width',
		'border-bottom-width',
		'border-left-width',
		'border-style',
		'padding-top',
		'padding-right',
		'padding-bottom',
		'padding-left',
		'font-style',
		'font-variant',
		'font-weight',
		'font-stretch',
		'font-size',
		'font-family',
		'line-height',
		'text-align',
		'text-transform',
		'text-indent',
		'letter-spacing',
		'word-spacing',
	];

	mirror.style.position = 'absolute';
	mirror.style.visibility = 'hidden';
	mirror.style.whiteSpace = 'pre-wrap';
	mirror.style.wordWrap = 'break-word';
	mirror.style.top = '0';
	mirror.style.left = '0';

	properties.forEach((property) => {
		mirror.style.setProperty(property, style.getPropertyValue(property));
	});

	mirror.textContent = textarea.value.substring(0, position);

	const marker = document.createElement('span');
	marker.textContent = '\u200b';
	mirror.appendChild(marker);
	document.body.appendChild(mirror);

	const fontSize = parseFloat(style.fontSize) || 16;
	const lineHeightText = style.lineHeight;
	let lineHeight = parseFloat(lineHeightText);
	if (!Number.isFinite(lineHeight)) {
		lineHeight = fontSize * 1.2;
	} else if (!lineHeightText.endsWith('px')) {
		lineHeight *= fontSize;
	}
	const borderTop = parseFloat(style.borderTopWidth) || 0;
	const top = marker.offsetTop + borderTop - textarea.scrollTop;

	document.body.removeChild(mirror);

	return { top, lineHeight };
}

/**
 * Multiline field that opens a filterable option list when `trigger` is typed.
 * Selecting an option replaces that trigger with the option value.
 */
export const SmartVariables = forwardRef<HTMLTextAreaElement, SmartVariablesProps>(function SmartVariables(props, ref) {
	const {
		value,
		onChange,
		options,
		placeholder,
		trigger = DEFAULT_TRIGGER,
		disabled = false,
		rows = 4,
		id,
		name,
		ariaLabel,
		className,
		textareaClassName,
		filterPlaceholder = 'Filter',
		emptyMessage = 'No options found',
		resize = 'vertical',
	} = props;

	const classes = useStyles();
	const listId = useId();
	const rootRef = useRef<HTMLDivElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	const filterRef = useRef<HTMLInputElement>(null);
	const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const triggerStartRef = useRef<number | null>(null);
	const openRef = useRef(false);
	const pendingCaretRef = useRef<number | null>(null);

	const [open, setOpen] = useState(false);
	const [filter, setFilter] = useState('');
	const [activeIndex, setActiveIndex] = useState(0);
	const [menuTop, setMenuTop] = useState<number | null>(null);

	const filteredOptions = useMemo(() => {
		const query = filter.trim().toLowerCase();
		if (!query) {
			return options;
		}

		return options.filter((option) => option.label.toLowerCase().includes(query));
	}, [filter, options]);

	const setTextareaRef = (node: HTMLTextAreaElement | null) => {
		textareaRef.current = node;
		if (typeof ref === 'function') {
			ref(node);
			return;
		}

		if (ref) {
			ref.current = node;
		}
	};

	const closeMenu = () => {
		openRef.current = false;
		triggerStartRef.current = null;
		setOpen(false);
		setFilter('');
		setActiveIndex(0);
		setMenuTop(null);
	};

	const measureMenuTop = () => {
		const textarea = textareaRef.current;
		const root = rootRef.current;
		const start = triggerStartRef.current;
		if (!textarea || !root || start == null) {
			return null;
		}

		const caret = getCaretCoordinates(textarea, start + trigger.length);
		const textareaOffsetTop = textarea.getBoundingClientRect().top - root.getBoundingClientRect().top;
		return textareaOffsetTop + caret.top + caret.lineHeight + 4;
	};

	const openMenu = (start: number) => {
		triggerStartRef.current = start;
		const alreadyOpen = openRef.current;
		openRef.current = true;
		const top = measureMenuTop();

		if (alreadyOpen) {
			setMenuTop(top);
			return;
		}

		flushSync(() => {
			setFilter('');
			setActiveIndex(0);
			setMenuTop(top);
			setOpen(true);
		});
		filterRef.current?.focus();
	};

	const insertOption = (option: SmartVariableOption) => {
		const start = triggerStartRef.current;
		if (start == null) {
			return;
		}

		const nextValue = value.slice(0, start) + option.value + value.slice(start + trigger.length);
		pendingCaretRef.current = start + option.value.length;
		onChange(nextValue);
		closeMenu();
	};

	useEffect(() => {
		if (pendingCaretRef.current == null) {
			return;
		}

		const textarea = textareaRef.current;
		const caret = pendingCaretRef.current;
		pendingCaretRef.current = null;
		textarea?.focus();
		textarea?.setSelectionRange(caret, caret);
	}, [value]);

	useEffect(() => {
		if (!open) {
			return;
		}

		setMenuTop(measureMenuTop());
		const textarea = textareaRef.current;
		const handlePositionChange = () => setMenuTop(measureMenuTop());
		textarea?.addEventListener('scroll', handlePositionChange);
		window.addEventListener('resize', handlePositionChange);

		return () => {
			textarea?.removeEventListener('scroll', handlePositionChange);
			window.removeEventListener('resize', handlePositionChange);
		};
	}, [open, value, trigger]);

	useEffect(() => {
		if (!open) {
			return;
		}

		optionRefs.current[activeIndex]?.scrollIntoView?.({ block: 'nearest' });
	}, [activeIndex, open, filteredOptions]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const handlePointerDown = (event: MouseEvent) => {
			const target = event.target;
			if (!(target instanceof Node)) {
				return;
			}

			if (rootRef.current?.contains(target)) {
				return;
			}

			closeMenu();
		};

		document.addEventListener('mousedown', handlePointerDown);
		return () => document.removeEventListener('mousedown', handlePointerDown);
	}, [open]);

	useEffect(() => {
		if (disabled && openRef.current) {
			closeMenu();
		}
	}, [disabled]);

	const handleTextareaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		const nextValue = event.target.value;
		const caret = event.target.selectionStart ?? nextValue.length;
		onChange(nextValue);

		if (disabled || !trigger) {
			return;
		}

		const textBeforeCaret = nextValue.slice(0, caret);
		if (textBeforeCaret.endsWith(trigger)) {
			openMenu(caret - trigger.length);
			return;
		}

		const start = triggerStartRef.current;
		if (openRef.current && (start == null || nextValue.slice(start, start + trigger.length) !== trigger)) {
			closeMenu();
		}
	};

	const handleTextareaSelect = (event: SyntheticEvent<HTMLTextAreaElement>) => {
		if (!openRef.current || document.activeElement !== event.currentTarget) {
			return;
		}

		const start = triggerStartRef.current;
		const caret = event.currentTarget.selectionStart ?? 0;
		if (start == null || caret !== start + trigger.length) {
			closeMenu();
		}
	};

	const handleTextareaKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		if (!open) {
			return;
		}

		if (event.key === 'Escape') {
			event.preventDefault();
			closeMenu();
			return;
		}

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			filterRef.current?.focus();
		}
	};

	const handleFilterKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.nativeEvent.isComposing) {
			return;
		}

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			if (filteredOptions.length === 0) {
				return;
			}
			setActiveIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
			return;
		}

		if (event.key === 'ArrowUp') {
			event.preventDefault();
			setActiveIndex((index) => Math.max(index - 1, 0));
			return;
		}

		if (event.key === 'Enter') {
			event.preventDefault();
			event.stopPropagation();
			const option = filteredOptions[activeIndex];
			if (option) {
				insertOption(option);
			}
			return;
		}

		if (event.key === 'Escape') {
			event.preventDefault();
			closeMenu();
			textareaRef.current?.focus();
			return;
		}

		if (event.key === 'Tab') {
			closeMenu();
		}
	};

	const handleOptionMouseDown = (event: ReactMouseEvent<HTMLButtonElement>, option: SmartVariableOption) => {
		event.preventDefault();
		insertOption(option);
	};

	const activeOptionId = filteredOptions[activeIndex] ? `${listId}-option-${activeIndex}` : undefined;

	return (
		<div className={mergeClasses(classes.root, className)} ref={rootRef}>
			<Textarea
				ref={setTextareaRef}
				className={mergeClasses(classes.textarea, textareaClassName)}
				id={id}
				name={name}
				rows={rows}
				appearance="outline"
				resize={resize}
				placeholder={placeholder}
				value={value}
				disabled={disabled}
				aria-label={ariaLabel}
				aria-autocomplete="list"
				aria-expanded={open}
				aria-controls={open ? listId : undefined}
				onChange={handleTextareaChange}
				onSelect={handleTextareaSelect}
				onKeyDown={handleTextareaKeyDown}
			/>
			{open && (
				<div
					className={classes.menu}
					style={menuTop == null ? undefined : { top: menuTop }}
				>
					<Input
						ref={filterRef}
						className={classes.filter}
						size="small"
						appearance="outline"
						placeholder={filterPlaceholder}
						value={filter}
						aria-label={filterPlaceholder}
						aria-controls={listId}
						aria-activedescendant={activeOptionId}
						aria-autocomplete="list"
						autoComplete="off"
						onChange={(_event, data) => {
							setFilter(data.value);
							setActiveIndex(0);
						}}
						onKeyDown={handleFilterKeyDown}
					/>
					<div
						id={listId}
						role="listbox"
						aria-label="Options"
						className={classes.list}
					>
						{filteredOptions.length === 0 ? (
							<div className={classes.empty} role="status">{emptyMessage}</div>
						) : (
							filteredOptions.map((option, index) => (
								<button
									key={`${option.label}-${option.value}-${index}`}
									id={`${listId}-option-${index}`}
									ref={(node) => {
										optionRefs.current[index] = node;
									}}
									type="button"
									role="option"
									aria-selected={index === activeIndex}
									className={mergeClasses(
										classes.option,
										index === activeIndex && classes.optionActive,
									)}
									onMouseEnter={() => setActiveIndex(index)}
									onMouseDown={(event) => handleOptionMouseDown(event, option)}
								>
									<span className={classes.optionLabel}>{option.label}</span>
									{option.value !== option.label && (
										<span className={classes.optionValue} aria-hidden="true">{option.value}</span>
									)}
								</button>
							))
						)}
					</div>
				</div>
			)}
		</div>
	);
});

export default SmartVariables;

const useStyles = makeStyles({
	root: {
		position: 'relative',
		width: '100%',
	},
	textarea: {
		width: '100%',
		minHeight: '96px',
		'::after': {
			borderBottom: 'none',
			height: 0,
		},
		':hover': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		},
		':focus-within': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1}`,
			borderBottomColor: `${tokens.colorNeutralStroke1}!important`,
			borderTopColor: `${tokens.colorNeutralStroke1}!important`,
			borderRightColor: `${tokens.colorNeutralStroke1}!important`,
			borderLeftColor: `${tokens.colorNeutralStroke1}!important`,
		},
	},
	menu: {
		position: 'absolute',
		top: '100%',
		left: 0,
		right: 0,
		zIndex: 1000,
		display: 'flex',
		flexDirection: 'column',
		gap: '8px',
		boxSizing: 'border-box',
		padding: '8px',
		backgroundColor: tokens.colorNeutralBackground1,
		border: `1px solid ${tokens.colorNeutralStroke1}`,
		borderRadius: tokens.borderRadiusMedium,
		boxShadow: tokens.shadow16,
	},
	filter: {
		width: '100%',
		'::after': {
			borderBottom: 'none',
			height: 0,
		},
		':hover': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		},
		':focus-within': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1}`,
			borderBottomColor: `${tokens.colorNeutralStroke1}!important`,
			borderTopColor: `${tokens.colorNeutralStroke1}!important`,
			borderRightColor: `${tokens.colorNeutralStroke1}!important`,
			borderLeftColor: `${tokens.colorNeutralStroke1}!important`,
		},
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		maxHeight: '240px',
		overflowY: 'auto',
		margin: 0,
		padding: 0,
	},
	option: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: '2px',
		width: '100%',
		margin: 0,
		padding: '8px 10px',
		border: 'none',
		borderRadius: tokens.borderRadiusMedium,
		backgroundColor: 'transparent',
		color: tokens.colorNeutralForeground1,
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase300,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase300,
		textAlign: 'left',
		cursor: 'pointer',
		':hover': {
			background: tokens.colorNeutralBackground4Hover,
		},
	},
	optionActive: {
		background: tokens.colorNeutralBackground4Hover,
		color: tokens.colorNeutralForeground1,
	},
	optionLabel: {
		width: '100%',
	},
	optionValue: {
		width: '100%',
		color: tokens.colorNeutralForeground3,
		fontFamily: tokens.fontFamilyMonospace,
		fontSize: tokens.fontSizeBase200,
		lineHeight: tokens.lineHeightBase200,
	},
	empty: {
		padding: '8px 10px',
		color: tokens.colorNeutralForeground3,
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase200,
		lineHeight: tokens.lineHeightBase200,
	},
});
